import { MXBaseComponent, AXClosbaleComponent, AXFocusableComponent, AXCommonModule } from '@acorex/components/common';
import * as i1 from '@angular/cdk/dialog';
import { DIALOG_DATA, Dialog, DialogModule } from '@angular/cdk/dialog';
import * as i2 from '@angular/cdk/portal';
import { TemplatePortal, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { TemplateRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, HostListener, inject, Injectable, NgModule } from '@angular/core';
import * as i3 from '@acorex/components/decorators';
import { AXDecoratorModule } from '@acorex/components/decorators';
import * as i4 from '@acorex/core/translation';
import { AXTranslationModule } from '@acorex/core/translation';
import * as i5 from '@angular/common';
import { AsyncPipe } from '@angular/common';
import { AXButtonModule } from '@acorex/components/button';
import { A11yModule } from '@angular/cdk/a11y';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { GlobalPositionStrategy } from '@angular/cdk/overlay';
import { BehaviorSubject } from 'rxjs';

class AXActionSheetComponent extends MXBaseComponent {
    /**
     *  @ignore
     */
    constructor(data, dialogRef) {
        super();
        this.data = data;
        this.dialogRef = dialogRef;
    }
    ngOnInit() {
        super.ngOnInit();
        if (this.data.content) {
            if (this.data.content instanceof TemplateRef) {
                this._selectedPortal = new TemplatePortal(this.data.content, this.getViewContainer(), {
                    $implicit: this.data,
                    ref: this,
                });
                this.cdr.markForCheck();
            }
            else if (typeof this.data.content === 'function') {
                this._selectedPortal = new ComponentPortal(this.data.content);
                this.cdr.markForCheck();
            }
        }
    }
    onKeydownHandler() {
        const focusedOrHasFocused = this.getHostElement().matches(':focus-within');
        if (this.data.closeButton && focusedOrHasFocused) {
            this.close(null);
        }
    }
    _handleAttched(ref) {
        ref = ref;
        if (ref.instance) {
            this._componentRef = ref.instance;
            Object.assign(this._componentRef, this.data);
            Object.assign(this._componentRef, { _isPopup: true });
            if (ref.instance.onClosed) {
                ref.instance.onClosed.subscribe((e) => {
                    this.close(e);
                });
            }
        }
    }
    onItemClick(item) {
        this.close(item);
        if (item?.onClick)
            item.onClick();
    }
    close(e) {
        this.dialogRef.close({
            component: this._componentRef,
            htmlElement: this.getHostElement(),
            data: e,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetComponent, deps: [{ token: DIALOG_DATA }, { token: i1.DialogRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXActionSheetComponent, selector: "ax-action-sheet", host: { listeners: { "keydown.escape": "onKeydownHandler()" } }, providers: [
            {
                provide: AXClosbaleComponent,
                useExisting: AXActionSheetComponent,
            },
            {
                provide: AXFocusableComponent,
                useExisting: AXActionSheetComponent,
            },
        ], usesInheritance: true, ngImport: i0, template: "@if (data.header) {\n  <ax-header>\n    <ax-prefix>\n      <ax-title>{{ data.title | translate | async }}</ax-title>\n\n      @if (data.subTitle) {\n        <ax-sub-title>\n          {{ data.subTitle }}\n        </ax-sub-title>\n      }\n    </ax-prefix>\n    @if (data.closeButton) {\n      <ax-suffix>\n        <ax-close-button></ax-close-button>\n      </ax-suffix>\n    }\n  </ax-header>\n}\n<ng-template [cdkPortalOutlet]=\"_selectedPortal\" (attached)=\"_handleAttched($event)\"></ng-template>\n<div class=\"ax-action-sheet-items\">\n  @for (item of data.items; let i = $index; track i) {\n    <div\n      class=\"ax-action-sheet-item ax-{{ item.color }}\"\n      [class.ax-state-disabled]=\"item.disabled\"\n      [tabindex]=\"i\"\n      (click)=\"onItemClick(item)\"\n    >\n      @if (item.icon) {\n        <span class=\"item-icon\" [class]=\"item.icon\"></span>\n      }\n      <span class=\"item-text\">{{ item.text | translate | async }}</span>\n    </div>\n  }\n</div>\n", styles: ["ax-action-sheet{display:block;width:100vw}@media (min-width: 768px){ax-action-sheet{width:70vw}}@media (min-width: 1024px){ax-action-sheet{width:50vw}}ax-action-sheet ax-header{padding:1rem;border-bottom-width:1px;font-size:1rem;line-height:1.5rem;font-weight:500}@media (min-width: 768px){ax-action-sheet ax-header{font-size:1.125rem;line-height:1.75rem}}ax-action-sheet ax-header ax-prefix,ax-action-sheet ax-header ax-suffix{display:flex;flex-direction:column;justify-content:flex-start}ax-action-sheet ax-header ax-prefix{align-items:flex-start}ax-action-sheet ax-header ax-prefix ax-title{overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:1}ax-action-sheet ax-header ax-suffix{align-items:flex-end;max-width:fit-content}ax-action-sheet .ax-action-sheet-items{padding-top:.5rem;padding-bottom:.5rem}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item{display:flex;padding:.75rem 1rem;align-items:center;cursor:pointer;-webkit-user-select:none;user-select:none}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item:hover{background-color:rgba(var(--ax-color-on-surface))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item .item-icon{margin-inline-end:.75rem;font-size:1.5rem;line-height:2rem}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-primary{color:rgba(var(--ax-color-primary-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-secondary{color:rgba(var(--ax-color-secondary-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-success{color:rgba(var(--ax-color-success-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-warning{color:rgba(var(--ax-color-warning-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-danger{color:rgba(var(--ax-color-danger-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-info{color:rgba(var(--ax-color-info-500))}\n"], dependencies: [{ kind: "directive", type: i2.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "component", type: i3.AXDecoratorCloseButtonComponent, selector: "ax-close-button", inputs: ["icon"] }, { kind: "component", type: i3.AXDecoratorGenericComponent, selector: "ax-footer, ax-header, ax-content, ax-divider, ax-form-hint, ax-prefix, ax-suffix, ax-text, ax-title, ax-sub-title, ax-placeholder, ax-overlay" }, { kind: "pipe", type: i4.AXTranslatorPipe, name: "translate" }, { kind: "pipe", type: i5.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-action-sheet', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [
                        {
                            provide: AXClosbaleComponent,
                            useExisting: AXActionSheetComponent,
                        },
                        {
                            provide: AXFocusableComponent,
                            useExisting: AXActionSheetComponent,
                        },
                    ], template: "@if (data.header) {\n  <ax-header>\n    <ax-prefix>\n      <ax-title>{{ data.title | translate | async }}</ax-title>\n\n      @if (data.subTitle) {\n        <ax-sub-title>\n          {{ data.subTitle }}\n        </ax-sub-title>\n      }\n    </ax-prefix>\n    @if (data.closeButton) {\n      <ax-suffix>\n        <ax-close-button></ax-close-button>\n      </ax-suffix>\n    }\n  </ax-header>\n}\n<ng-template [cdkPortalOutlet]=\"_selectedPortal\" (attached)=\"_handleAttched($event)\"></ng-template>\n<div class=\"ax-action-sheet-items\">\n  @for (item of data.items; let i = $index; track i) {\n    <div\n      class=\"ax-action-sheet-item ax-{{ item.color }}\"\n      [class.ax-state-disabled]=\"item.disabled\"\n      [tabindex]=\"i\"\n      (click)=\"onItemClick(item)\"\n    >\n      @if (item.icon) {\n        <span class=\"item-icon\" [class]=\"item.icon\"></span>\n      }\n      <span class=\"item-text\">{{ item.text | translate | async }}</span>\n    </div>\n  }\n</div>\n", styles: ["ax-action-sheet{display:block;width:100vw}@media (min-width: 768px){ax-action-sheet{width:70vw}}@media (min-width: 1024px){ax-action-sheet{width:50vw}}ax-action-sheet ax-header{padding:1rem;border-bottom-width:1px;font-size:1rem;line-height:1.5rem;font-weight:500}@media (min-width: 768px){ax-action-sheet ax-header{font-size:1.125rem;line-height:1.75rem}}ax-action-sheet ax-header ax-prefix,ax-action-sheet ax-header ax-suffix{display:flex;flex-direction:column;justify-content:flex-start}ax-action-sheet ax-header ax-prefix{align-items:flex-start}ax-action-sheet ax-header ax-prefix ax-title{overflow:hidden;display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:1}ax-action-sheet ax-header ax-suffix{align-items:flex-end;max-width:fit-content}ax-action-sheet .ax-action-sheet-items{padding-top:.5rem;padding-bottom:.5rem}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item{display:flex;padding:.75rem 1rem;align-items:center;cursor:pointer;-webkit-user-select:none;user-select:none}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item:hover{background-color:rgba(var(--ax-color-on-surface))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item .item-icon{margin-inline-end:.75rem;font-size:1.5rem;line-height:2rem}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-primary{color:rgba(var(--ax-color-primary-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-secondary{color:rgba(var(--ax-color-secondary-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-success{color:rgba(var(--ax-color-success-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-warning{color:rgba(var(--ax-color-warning-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-danger{color:rgba(var(--ax-color-danger-500))}ax-action-sheet .ax-action-sheet-items .ax-action-sheet-item.ax-info{color:rgba(var(--ax-color-info-500))}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DIALOG_DATA]
                }] }, { type: i1.DialogRef }], propDecorators: { onKeydownHandler: [{
                type: HostListener,
                args: ['keydown.escape']
            }] } });

class AXActionSheetService {
    constructor() {
        this.dialog = inject(Dialog);
    }
    open(config) {
        const defaultConfig = {
            title: 'action-sheet.title',
            closeButton: true,
            closeOnBackdropClick: true,
            header: true,
        };
        config = Object.assign(defaultConfig, config);
        const dialogRef = this.dialog.open(AXActionSheetComponent, {
            data: config,
            autoFocus: 'first-tabbable',
            restoreFocus: true,
            role: 'dialog',
            ariaModal: true,
            closeOnNavigation: true,
            closeOnDestroy: true,
            hasBackdrop: true,
            panelClass: ['ax-actionsheet-panel'],
            disableClose: config.closeOnBackdropClick ? false : true,
            positionStrategy: new GlobalPositionStrategy().centerHorizontally().bottom(),
        });
        const promise = new Promise((resolve) => {
            const closed = new BehaviorSubject(null);
            const axDialogRef = {
                close: (e) => {
                    dialogRef.close(e);
                },
                closed,
            };
            dialogRef.closed.subscribe((c) => {
                if (c?.data) {
                    closed.next({ data: c.data });
                }
                else {
                    closed.next({});
                }
            });
            if (resolve) {
                resolve(axDialogRef);
            }
        });
        return promise;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetService, decorators: [{
            type: Injectable
        }] });

const MODULES = [
    AXCommonModule,
    DragDropModule,
    A11yModule,
    AXButtonModule,
    PortalModule,
    AXDecoratorModule,
    AXTranslationModule,
    DialogModule,
    AsyncPipe,
];
class AXActionSheetModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetModule, declarations: [AXActionSheetComponent], imports: [AXCommonModule,
            DragDropModule,
            A11yModule,
            AXButtonModule,
            PortalModule,
            AXDecoratorModule,
            AXTranslationModule,
            DialogModule,
            AsyncPipe], exports: [AXActionSheetComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetModule, providers: [AXActionSheetService], imports: [AXCommonModule,
            DragDropModule,
            A11yModule,
            AXButtonModule,
            PortalModule,
            AXDecoratorModule,
            AXTranslationModule,
            DialogModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXActionSheetModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [AXActionSheetComponent],
                    imports: [...MODULES],
                    exports: [AXActionSheetComponent],
                    providers: [AXActionSheetService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXActionSheetComponent, AXActionSheetModule, AXActionSheetService };
